<template>
    <div class="v-date-range">
        <v-menu
                v-model="menu"
                :close-on-content-click="false"
                offset-y
                v-bind="menuProps"
        >
            <template v-slot:activator="{ on }">
            <v-text-field
                    slot="activator"
                    class="v-date-range__input-field"
                    :value="inputValue"
                    :placeholder="placeholder"
                    v-bind="inputProps"
                    @click="menu = true"
                    outlined
            ></v-text-field>
            </template>
            <v-card class="v-date-range__menu-content">
                <v-card-text>
                    <div
                            :data-days="highlightDates.length"
                            :class="{
              'v-date-range__pickers': true,
              'v-date-range--highlighted': highlightDates.length
            }"
                    >
                        <v-card-title v-if="$slots.title">
                            <slot name="title" v-if="$slots.title"></slot>
                        </v-card-title>
                        <v-card-text>
                            <v-row class="v-date-range__content">
                                <v-col cols="12" md="12">
                                    <v-date-picker
                                            class="mr-4 v-date-range__picker--start v-date-range__picker"
                                            v-model="pickerStart"
                                            :locale="locale"
                                            :first-day-of-week="firstDayOfWeek"
                                            :min="min"
                                            :max="max"
                                            :no-title="noTitle"
                                            :next-icon="nextIcon"
                                            :prev-icon="prevIcon"
                                            :events="highlightDates"
                                            :event-color="highlightClasses"
                                    ></v-date-picker>
                                    <v-date-picker
                                        class="v-date-range__picker--end v-date-range__picker"
                                        v-model="pickerEnd"
                                        :locale="locale"
                                        :first-day-of-week="firstDayOfWeek"
                                        :min="min"
                                        :max="max"
                                        :no-title="noTitle"
                                        :next-icon="nextIcon"
                                        :prev-icon="prevIcon"
                                        :events="highlightDates"
                                        :event-color="highlightClasses"
                                        @change="applyRange"
                                ></v-date-picker>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </div>
                </v-card-text>
                <v-card-actions class="calendar-cta-wrapper">
                    <v-btn class="v-btn-smb" text @click="reset">Clear</v-btn>
                    <v-btn class="v-btn-smb" text @click="menu = false">Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-menu>
    </div>
</template>
<script>
    import { format, parse, differenceInCalendarDays, addDays } from 'date-fns';
    const isoFormat = 'yyyy-MM-dd';
    const defaultDate = format(new Date(), isoFormat);

    export default {
        name: 'v-daterange',
        props: {
            value: {
                type: Object,
                default: () => {
                    return { start: defaultDate, end: defaultDate };
                }
            },
            disabled: {
                type: Boolean,
                default: false
            },
            presets: {
                type: Array,
                default: () => {
                    return [];
                }
            },
            noPresets: {
                type: Boolean,
                default: false
            },
            startLabel: {
                type: String,
                default: 'Start Date'
            },
            endLabel: {
                type: String,
                default: 'End Date'
            },
            separatorLabel: {
                type: String,
                default: '-'
            },
            presetLabel: {
                type: String,
                default: 'Presets'
            },
            min: {
                type: String,
                default: undefined
            },
            max: {
                type: String,
                default: undefined
            },
            locale: {
                type: String,
                default: 'en-us'
            },
            firstDayOfWeek: {
                type: [String, Number],
                default: 0
            },
            noTitle: {
                type: Boolean,
                default: false
            },
            displayFormat: {
                type: String
            },
            highlightColor: {
                type: String,
                default: 'blue lighten-5'
            },
            showReset: {
                type: Boolean,
                default: true
            },
            nextIcon: {
                type: String,
                default: '$vuetify.icons.next'
            },
            prevIcon: {
                type: String,
                default: '$vuetify.icons.prev'
            },
            inputProps: {
                type: Object,
                default: () => {
                    return {};
                }
            },
            menuProps: {
                type: Object,
                default: () => {
                    return {};
                }
            }
        },
        data() {
            return {
                menu: false,
                pickerStart: this.value.start,
                pickerEnd: this.value.end,
                highlightDates: [],
                highlightClasses: {}
            };
        },
        computed: {
            inputValue() {
                if (this.isValueEmpty) {
                    return '';
                }
                const start = this.displayFormat
                    ? this.formatDate(this.value.start, this.displayFormat)
                    : this.value.start;
                const end = this.displayFormat
                    ? this.formatDate(this.value.end, this.displayFormat)
                    : this.value.end;
                return `${start}    ${this.separatorLabel}     ${end}`;
            },
            placeholder() {
                return `${this.startLabel}    ${this.separatorLabel}    ${this.endLabel}`;
            },
            isValueEmpty() {
                return !this.value.start;
            },
            bothSelected() {
                return this.pickerStart && this.pickerEnd;
            },
            isPresetActive() {
                return this.presets.map(
                    preset =>
                        preset.range[0] === this.pickerStart &&
                        preset.range[1] === this.pickerEnd
                );
            }
        },
        methods: {
            applyRange() {
                this.menu = false;
                this.emitRange();
            },
            closeMenu() {
                this.pickerStart = this.value.start;
                this.pickerEnd = this.value.end;
                this.highlight();
                this.$emit('menu-closed');
            },
            formatDate(date, fmt) {
                return format(parse(date), fmt);
            },
            highlight() {
                if (!this.bothSelected) {
                    return;
                }
                const dates = [];
                const classes = {};
                const start = parse(this.pickerStart);
                const end = parse(this.pickerEnd);
                const diff = Math.abs(differenceInCalendarDays(start, end));

                for (let i = 0; i <= diff; i++) {
                    const date = format(addDays(start, i), isoFormat);
                    dates.push(date);
                    const classesArr = [];
                    classesArr.push(`v-date-range__in-range`);
                    classesArr.push(this.highlightColor);
                    i === 0 && classesArr.push(`v-date-range__range-start`);
                    i === diff && classesArr.push(`v-date-range__range-end`);
                    classes[date] = classesArr.join(' ');
                }
                this.highlightDates = dates;
                this.highlightClasses = classes;
            },
            selectPreset(presetIndex) {
                this.pickerStart = this.presets[presetIndex].range[0];
                this.pickerEnd = this.presets[presetIndex].range[1];
            },
            reset() {
                this.pickerStart = '';
                this.pickerEnd = '';
                this.highlightDates = [];
                this.highlightClasses = {};
                this.emitRange();
            },
            emitRange() {
                this.$emit('input', {
                    start: this.pickerStart,
                    end: this.pickerEnd
                });
            }
        },
    };
</script>
<style scoped lang="stylus">
    .v-date-range__input-field >>> input {
        text-align: center;
    }
    .v-date-range__content {
        display: flex;

    >>> .v-date-picker-table {
    .v-btn {
        border-radius: 0;
    }
    }
    }
    .v-date-range__pickers >>> .v-date-picker-table__events {
        height: 100%;
        width: 100%;
        top: 0;
        z-index: -1;
    }
    .v-date-range__pickers >>> .v-date-picker-table table {
        width: auto;
        margin: auto;
        border-collapse: collapse;

    & th, & td {
                height: 32px;
                width: 32px;
            }

    & td {
    .v-btn {
    &.v-btn--outline {
         border: none;
         box-shadow: 0 0 0 1px currentColor inset;
     }

    &.v-btn--active::before {
         background-color: transparent !important;
     }
    }
    }
    }
    .v-date-range__pickers >>> .v-date-range__in-range {
        height: 100%;
        width: 100%;
        margin: 0;
        border-radius: 0;
    }
    .theme--light.v-sheet {
        background-color: #fff;
    }
    .v-menu__content {
        border-radius: 5px;
        -webkit-box-shadow: 0px 0px 15px 0px rgba(23,37,48,0.10);
        -moz-box-shadow: 0px 0px 15px 0px rgba(23,37,48,0.10);
        box-shadow: 0px 0px 15px 0px rgba(23,37,48,0.10);
    }
    .theme--light.v-card {
        -webkit-box-shadow: 0px 0px 15px 0px rgba(23,37,48,0.10);
        -moz-box-shadow: 0px 0px 15px 0px rgba(23,37,48,0.10);
        box-shadow: 0px 0px 15px 0px rgba(23,37,48,0.10) !important;
    }
    .calendar-cta-wrapper {
        justify-content: flex-end;
        padding: 0 20px 20px 20px;
    }
    .v-menu__content.theme--light.v-menu__content--fixed.menuable__content__active {
        padding-bottom: 10px;
        height: 100%;
        background: #fff;
    }
    @media only screen and (min-width: 768px) {
        .v-menu__content {
            border-radius: 15px;
        }
    }
</style>